import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Tag, Download, Library, Loader2, Trash2, ShieldCheck, Scissors, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/components/riptag/util";
import { TrackSplitter } from "@/components/riptag/TrackSplitter";
import { ArtworkPanel } from "@/components/riptag/ArtworkPanel";
import { appConfirm } from "@/components/AppDialogHost";

const VARIANTS = ["original", "clean", "explicit", "radio_edit", "live", "remaster"];
const ROTATIONS = ["unassigned", "heavy", "medium", "light"];
const QUALITY_CLS = {
  strong: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
  possible: "border-amber-500/50 bg-amber-500/10 text-amber-300",
  weak: "border-line text-ink-muted",
};

export const MetaEditor = ({ track, onChanged }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [dlFormat, setDlFormat] = useState("original");
  const [showSplit, setShowSplit] = useState(false);

  useEffect(() => {
    if (track) {
      setForm({ title: track.title || "", artist: track.artist || "", album: track.album || "",
        genre: track.genre || "", year: track.year || "", track_no: track.track_no || 0,
        variant: track.variant || "original", rotation: track.rotation || "unassigned" });
    }
  }, [track]);

  if (!track) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-6 text-center text-sm text-ink-muted" data-testid="rip-editor-empty">
        Select a track to edit its metadata.
      </div>
    );
  }

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy("");
  };

  const save = () => run("save", () => api.put(`/rip/tracks/${track.track_id}`, {
    ...form, track_no: Number(form.track_no) || 0 }), "Metadata saved");

  const writeTags = () => run("tags", async () => {
    await api.put(`/rip/tracks/${track.track_id}`, { ...form, track_no: Number(form.track_no) || 0 });
    const r = await api.post(`/rip/tracks/${track.track_id}/write-tags`);
    toast.success(r.data.art_embedded ? "Tags + cover art written into the file" : "Tags written (no cover art found)");
  });

  const identify = () => run("ident", async () => {
    const r = await api.post(`/rip/tracks/${track.track_id}/identify`, {});
    if (r.data.status === "auto_applied") toast.success(`Auto-tagged: ${r.data.applied.title} — ${r.data.applied.artists}`);
    else if (r.data.status === "suggested") toast.info(`${r.data.suggestions.length} possible match${r.data.suggestions.length === 1 ? "" : "es"} — confirm below`);
    else toast.warning("No match found — tag manually or try the MusicBrainz album match");
  });

  const applySuggestion = (i) => run("ident", async () => {
    await api.post(`/rip/tracks/${track.track_id}/identify/apply`, { index: i });
    toast.success("Match applied — original metadata kept for undo");
  });

  const undoIdentify = () => run("ident",
    () => api.post("/rip/meta/restore", { scope: "track", id: track.track_id }),
    "Original metadata restored");

  const download = () => run("dl", async () => {
    if (dlFormat === "original") {
      const r = await api.get(`/rip/tracks/${track.track_id}/download`, { responseType: "blob" });
      downloadBlob(r.data, `${form.artist || "Unknown"} - ${form.title || "Track"}.${track.format}`);
    } else {
      const r = await api.get(`/rip/tracks/${track.track_id}/download-as`,
        { params: { format: dlFormat }, responseType: "blob" });
      downloadBlob(r.data, `${form.artist || "Unknown"} - ${form.title || "Track"}.${dlFormat}`);
    }
  });

  const field = (label, key, props = {}) => (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">{label}</div>
      <input value={form[key] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        data-testid={`rip-edit-${key}`} {...props}
        className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-sm focus:border-brand-amber focus:outline-none" />
    </div>
  );

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="rip-meta-editor">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-muted">Track Metadata</div>
        <button onClick={() => run("del", async () => {
          if (await appConfirm("Delete this track and its file?")) await api.delete(`/rip/tracks/${track.track_id}`);
        })} data-testid="rip-delete-track-btn" className="text-ink-muted hover:text-red-400 transition">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <ArtworkPanel track={track} onChanged={onChanged} />

      {field("Title", "title")}
      {field("Artist", "artist")}
      {field("Album", "album")}
      <div className="grid grid-cols-3 gap-2">
        {field("Genre", "genre")}
        {field("Year", "year")}
        {field("Track #", "track_no", { type: "number" })}
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">
          Variant <span className="text-amber-300/80 normal-case">(never auto-merged)</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VARIANTS.map((v) => (
            <button key={v} onClick={() => setForm((f) => ({ ...f, variant: v }))}
              data-testid={`rip-variant-${v}`}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase border transition ${
                form.variant === v ? "bg-brand-amber text-black border-brand-amber" : "border-line text-ink-secondary hover:text-ink-primary"}`}>
              {v.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Rotation Category</div>
        <select value={form.rotation} onChange={(e) => setForm((f) => ({ ...f, rotation: e.target.value }))}
          data-testid="rip-rotation-select"
          className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-sm focus:border-brand-amber focus:outline-none">
          {ROTATIONS.map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
        </select>
      </div>

      {track.source && (
        <div className="text-[10px] font-mono text-ink-muted">
          Source: {track.source}{track.mb_confidence ? ` · confidence ${track.mb_confidence}%` : ""}
        </div>
      )}

      {track.dup_status === "flagged" && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11px] text-amber-200">
          ⚠ {track.dup_reason}
          <button onClick={() => run("dup", () => api.post(`/rip/tracks/${track.track_id}/dup-dismiss`), "Marked as not a duplicate")}
            data-testid="rip-dup-dismiss-btn"
            className="ml-2 inline-flex items-center gap-1 underline hover:text-amber-100">
            <ShieldCheck className="h-3 w-3" /> Not a duplicate
          </button>
        </div>
      )}

      {track.verify_status && (
        <div className={`rounded-md border p-2.5 text-[11px] ${
          track.verify_status === "ok" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : track.verify_status === "warning" ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
            : "border-rose-500/40 bg-rose-500/10 text-rose-200"}`} data-testid="rip-verify-info">
          <div className="font-bold">
            {track.verify_status === "ok" ? "✓ Audio verified" : track.verify_status === "warning" ? "⚠ Audio warnings" : "✗ Audio problems"}
            <span className="font-normal font-mono ml-1">
              {track.verified_duration ? `${Math.floor(track.verified_duration / 60)}:${String(Math.round(track.verified_duration % 60)).padStart(2, "0")}` : ""}
              {track.verified_bitrate ? ` · ${track.verified_bitrate}kbps` : ""}
              {track.verified_sample_rate ? ` · ${(track.verified_sample_rate / 1000).toFixed(1)}kHz` : ""}
              {track.verified_codec ? ` · ${track.verified_codec}` : ""}
            </span>
          </div>
          {(track.verify_issues || []).map((i, n) => <div key={n} className="mt-0.5">{i}</div>)}
        </div>
      )}

      <button onClick={identify} disabled={!!busy} data-testid="rip-identify-btn"
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-brand-cyan/50 text-brand-cyan px-3 py-2 text-xs font-bold hover:bg-brand-cyan/10 transition disabled:opacity-50">
        {busy === "ident" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />}
        {busy === "ident" ? "Identifying…" : "Identify song"}
      </button>

      {(track.identify_suggestions || []).length > 0 && (
        <div className="rounded-md border border-line p-2.5 space-y-2" data-testid="rip-ident-suggestions">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
            Song matches
            {track.identify_status === "auto_applied" ? " · auto-applied" :
              track.identify_status === "applied" ? " · applied" : " · needs your confirmation"}
          </div>
          {(track.identify_suggestions || []).slice(0, 3).map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`rip-ident-row-${i}`}>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase border shrink-0 ${QUALITY_CLS[s.quality] || QUALITY_CLS.weak}`}>
                {s.quality}{s.score_pct ? ` ${s.score_pct}%` : ""}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs truncate">{s.title} — {s.artists || "Unknown artist"}</div>
                <div className="text-[10px] text-ink-muted truncate">
                  {s.album ? `${s.album} · ` : ""}
                  {s.variant ? `${s.variant.replace("_", " ")} · ` : ""}
                  {s.source === "ai_lyrics" ? "AI lyrics match" : "AcoustID fingerprint"}
                </div>
              </div>
              <button onClick={() => applySuggestion(i)} disabled={!!busy} data-testid={`rip-ident-apply-${i}`}
                className="rounded-md border border-line px-2 py-1 text-[10px] font-bold hover:border-brand-amber/60 transition disabled:opacity-50 shrink-0">
                Apply
              </button>
            </div>
          ))}
          {["auto_applied", "applied"].includes(track.identify_status) && (
            <button onClick={undoIdentify} disabled={!!busy} data-testid="rip-ident-undo-btn"
              className="text-[10px] underline text-ink-muted hover:text-ink-primary">
              Undo — restore original metadata
            </button>
          )}
        </div>
      )}

      {(track.duration || 0) >= 120 && (
        <button onClick={() => setShowSplit(true)} data-testid="rip-split-open-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-brand-amber/50 text-brand-amber px-3 py-2 text-xs font-bold hover:bg-brand-amber/10 transition">
          <Scissors className="h-3.5 w-3.5" /> Split into tracks (long recording)
        </button>
      )}

      {showSplit && (
        <TrackSplitter track={track} onClose={() => setShowSplit(false)}
          onDone={() => { setShowSplit(false); onChanged(); }} />
      )}

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={save} disabled={!!busy} data-testid="rip-save-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-line px-3 py-2 text-xs font-bold hover:border-brand-amber/60 transition disabled:opacity-50">
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
        <button onClick={writeTags} disabled={!!busy} data-testid="rip-write-tags-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-amber text-black px-3 py-2 text-xs font-bold hover:opacity-90 transition disabled:opacity-50">
          {busy === "tags" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Tag className="h-3.5 w-3.5" />} Write Tags
        </button>
        <button onClick={download} disabled={!!busy} data-testid="rip-download-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-line px-3 py-2 text-xs font-bold hover:border-brand-cyan/60 transition disabled:opacity-50">
          {busy === "dl" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download
        </button>
        <select value={dlFormat} onChange={(e) => setDlFormat(e.target.value)} data-testid="rip-dl-format"
          className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none">
          <option value="original">Original ({(track.format || "").toUpperCase()})</option>
          <option value="mp3">Convert to MP3</option>
          <option value="wav">Convert to WAV</option>
          <option value="flac">Convert to FLAC</option>
        </select>
        <button disabled={!!busy || track.in_library} data-testid="rip-add-library-btn"
          onClick={() => run("lib", () => api.post(`/rip/tracks/${track.track_id}/library`), "Added to Music Library")}
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-cyan/90 text-black px-3 py-2 text-xs font-bold hover:opacity-90 transition disabled:opacity-50">
          {busy === "lib" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Library className="h-3.5 w-3.5" />}
          {track.in_library ? "In Library" : "Add to Library"}
        </button>
      </div>
    </div>
  );
};
